import { Plus, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  removeImage,
  setProducts,
  updateImage,
} from "../../../../redux/slices/productSlice";
import {
  onDeleteCatalogImage,
  onUploadCatalogImage,
} from "../../../../redux/homeAction";

export default function ImageUploader({ activeIndex, setLoading, catalogId }) {
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.product);

  const current = products?.[activeIndex];

  const extraImages = Array.isArray(current?.images)
    ? current.images.filter((img) => img.type === "extra")
    : [];

  const addExtraImage = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("catalogId", catalogId);
    formData.append("productIndex", activeIndex);
    formData.append("type", "extra");

    const res = await dispatch(onUploadCatalogImage(formData, setLoading));

    const updated = structuredClone(products);

    if (!Array.isArray(updated[activeIndex].extraImages)) {
      updated[activeIndex].extraImages = [];
    }

    updated[activeIndex].images.push({
      type: "extra",
      url: res.data.urls?.original,
    });

    dispatch(setProducts(updated));
  };

  const removeExtraImage = async (index) => {
    const updated = structuredClone(products);

    const product = updated[activeIndex];

    const extras = product.images.filter((img) => img.type === "extra");
    const target = extras[index];

    product.images = product.images.filter(
      (img) => !(img.type === "extra" && img.url === target.url),
    );

    await dispatch(
      onDeleteCatalogImage(
        {
          type: "extra",
          catalogId: catalogId,
          productId: current?._id,
          url: target.url,
        },
        setLoading,
      ),
    );

    dispatch(setProducts(updated));
  };

  const normalizeProduct = (product) => ({
    ...product,
    images: (product.images || []).map((img) => ({
      type: img.type,
      urls: img.urls || {
        original: img.url || "",
        thumb: img.url || "",
        medium: img.url || "",
        large: img.url || "",
      },
    })),
  });

  const uploadRequiredImage = async (type, file) => {
    try {
      const formData = new FormData();
      formData.append("catalogId", catalogId);
      formData.append("productIndex", activeIndex);
      formData.append("type", type);
      formData.append("image", file);

      const res = await dispatch(onUploadCatalogImage(formData, setLoading));
      // const normalized = res.data.draft.products.map(normalizeProduct);

      dispatch(
        updateImage({
          index: activeIndex,
          type,
          url: res.data.urls?.original,
        }),
      );
    } catch (err) {}
  };

  const getImage = (images, type) => {
    if (!Array.isArray(images)) return "";

    const img = images.find((i) => i.type === type);

    return img?.urls?.original || img?.urls?.medium || img?.url || "";
  };

  const handleRemoveImage = async (type) => {
    try {
      const image = getImage(current?.images, type);
      await dispatch(
        onDeleteCatalogImage(
          {
            type: type,
            catalogId: catalogId,
            productId: current?._id,
            url: image,
          },
          setLoading,
        ),
      );

      dispatch(
        removeImage({
          index: activeIndex,
          type,
        }),
      );
    } catch (err) {}
  };

  const frontImage = getImage(current?.images, "front");

  const normalizeImage = (img) => {
    if (!img) return null;

    return {
      type: img.type,
      url: img.urls?.original || img.urls?.medium || img.url || "",
    };
  };

  return (
    <div className="space-y-5 w-full max-w-md mx-auto px-2">
      {/* MAIN IMAGE */}
      <div>
        <h4 className="text-sm font-semibold mb-2">Main Product Image</h4>

        <div className="border rounded-lg p-2 bg-gray-50 h-28 flex items-center justify-center">
          {frontImage ? (
            <img src={frontImage} className="h-full w-full object-contain" />
          ) : (
            <label className="cursor-pointer text-xs text-indigo-600">
              Upload Main Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) =>
                  uploadRequiredImage("front", e.target.files[0])
                }
              />
            </label>
          )}
        </div>
      </div>

      {/* REQUIRED IMAGES (NO UPLOAD BUTTON HERE) */}
      <div>
        <h4 className="text-sm font-semibold mb-2">Required Images</h4>

        <div className="space-y-2">
          {["front", "side", "back", "zoom"].map((type) => {
            const imageUrl = getImage(current?.images, type);
            return (
              <div key={type}>
                {imageUrl && <img src={imageUrl} />}

                <label className="cursor-pointer text-indigo-600">
                  Upload
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      uploadRequiredImage(type, e.target.files[0])
                    }
                  />
                </label>
                {imageUrl && (
                  <button onClick={() => handleRemoveImage(type)}>
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2">Extra Images</h4>
        <div className="grid grid-cols-3 gap-2">
          {extraImages.map((img, i) => (
            <div key={i} className="relative">
              <img
                src={normalizeImage(img).url}
                className="h-16 w-full object-cover rounded border"
              />

              <button
                onClick={() => removeExtraImage(i)}
                className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"
              >
                <X size={12} />
              </button>
            </div>
          ))}

          {extraImages.length < 9 && (
            <label className="h-16 border flex items-center justify-center cursor-pointer bg-gray-50 rounded">
              <Plus size={16} />
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => addExtraImage(e.target.files[0])}
              />
            </label>
          )}
        </div>
      </div>

      {/* EXTRA IMAGES */}
    </div>
  );
}
